import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { ITag } from '@bloomays-lib/types.shared';
import { useTheme } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material/TextField';

export type AutoCompleteTagProps = {
  setTag: React.Dispatch<React.SetStateAction<ITag[]>>;
  collectTags: (input: string) => Promise<ITag[]>;
  loading?: boolean;
  label: string;
  placeholder: string;
};

const AutoCompleteTag = ({ collectTags, loading, setTag, label, placeholder }: AutoCompleteTagProps) => {
  const [options, setOptions] = React.useState<readonly ITag[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const { t } = useTranslation(['random']);
  const theme = useTheme();
  const fetchTags = React.useMemo(
    () =>
      throttle((args: { input: string }, callback: (results?: readonly ITag[]) => void) => {
        collectTags(args.input).then(callback).catch(console.error);
      }, 500),
    [collectTags],
  );

  React.useEffect(() => {
    let newOptions: readonly ITag[] = [];
    if (inputValue === '') {
      setOptions([]);
      return undefined;
    }

    fetchTags({ input: inputValue }, (results?: readonly ITag[]) => {
      if (results) {
        newOptions = [...newOptions, ...results];
        setOptions(results);
      }
    });
  }, [inputValue, fetchTags]);

  return (
    <Autocomplete
      loading={loading}
      loadingText={t('loadingText')}
      noOptionsText={t('noOptionsText')}
      fullWidth={true}
      size="small"
      sx={{
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: theme.palette.blue[60],
        },

        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
      }}
      multiple
      limitTags={3}
      filterOptions={(x: ITag[]) => x}
      id="multiple-limit-tags"
      options={options}
      getOptionLabel={(option: ITag) => {
        return option.value || '';
      }}
      onChange={(event: any, newValue: readonly ITag[]) => {
        if (newValue) {
          setOptions(newValue.concat(options));
          setTag([...newValue]);
        }
      }}
      onInputChange={(event: unknown, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      renderValue={(value: { value: string }[], getTagProps: (element: any) => any) => {
        return value.map((option, index: number) => {
          return <Chip key={index} label={option.value} {...getTagProps({ index })} />;
        });
      }}
      renderInput={(params: TextFieldProps) => <TextField {...params} label={label} placeholder={placeholder} />}
    />
  ) as React.ReactNode;
};

export default AutoCompleteTag;
export { AutoCompleteTag };
